import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CalendarOptions, EventMountArg } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityVisibilityEnum,
  TimeslotInterface,
} from '@sport-activities/sdk';
import { add } from 'date-fns';
import { forkJoin, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { BuildTabButtonsGroupConfigInterface } from '~/component/tab-buttons-group/interfaces/build-config.interface';
import { TabButtonsGroupConfigInterface } from '~/component/tab-buttons-group/interfaces/config.interface';
import { TAILWIND_CONFIG } from '~/core/core.module';
import { dedupe } from '~/core/helpers/array/dedupe/dedupe.helper';
import { setDateTime } from '~/core/helpers/date/date.helper';
import { StripHtmlPipe } from '~/core/pipes/stripHtml.pipe';
import { ActivityService } from '~/core/services/activity/activity.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { FullCalendarEventAdapter } from '~/organizer/shared/components/shared/calendar/adapters/event/fullcalendar-event.adapter';
import { OrganizerSharedCalendarViewEnum } from '~/organizer/shared/enums/calendar-view.enum';

import { FullCalendarViewEnum } from './enums/full-calendar-view.enum';
import { CalendarCreationTimeslotInterface } from './interfaces/recurrent-event.interface';

import resolveConfig from 'tailwindcss/resolveConfig';

import tailwindConfig from '~/../../tailwind.config.js';

const fullConfig = resolveConfig(tailwindConfig);

const CALENDAR_CONFIG_MOBILE = {
  left: 'today',
  center: 'title',
  right: 'prev,next',
};

const CALENDAR_CONFIG_DESKTOP = {
  left: 'prev,next today',
  center: 'title',
  right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
};

/**
 * This component need AppWindowSize to work
 */
@Component({
  selector: 'app-organizer-shared-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OrganizerSharedCalendarComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Output() private readonly updateDateRange = new EventEmitter<{
    endDate: string;
    startDate: string;
  }>();

  @Input() public clickableTimeslots = true;
  @Input()
  public creationTimeslots: CalendarCreationTimeslotInterface<Date>[] = [];
  @Input() public showViews = true;
  @Input() public timeslots: TimeslotInterface[] = [];
  @Input() public view = OrganizerSharedCalendarViewEnum.Months;

  @ViewChild('calendar') public calendarComponent: ElementRef;

  private readonly subscriptions = new Subscription();
  private readonly viewsConfig: BuildTabButtonsGroupConfigInterface[] = [
    {
      textKey: 'organizer_planning.planning.views.months',
      value: OrganizerSharedCalendarViewEnum.Months,
    },
    {
      textKey: 'organizer_planning.planning.views.weeks',
      value: OrganizerSharedCalendarViewEnum.Weeks,
    },
    {
      textKey: 'organizer_planning.planning.views.days',
      value: OrganizerSharedCalendarViewEnum.Days,
    },
    {
      textKey: 'organizer_planning.planning.views.list',
      value: OrganizerSharedCalendarViewEnum.List,
    },
  ];

  private calendar;
  private currentCalendarInterval: {
    endDate: string;
    startDate: string;
  };

  public calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    height: 'auto',
    events: [],
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    datesSet: (datesSetEvent: any) => {
      // toISOString => TZ0
      // StartStr & endStr => GMT+01:00 Waiting API support.
      const interval = {
        startDate: datesSetEvent.start.toISOString(), // datesSetEvent.startStr,
        endDate: datesSetEvent.end.toISOString(), // datesSetEvent.endStr,
      };

      this.updateDateRange.emit(interval);
      this.currentCalendarInterval = interval;
    },
  };
  public tabButtonsGroupViewsConfig: TabButtonsGroupConfigInterface[] = [];

  constructor(
    @Inject(TAILWIND_CONFIG) private readonly tailwindConfig,
    private readonly matchMediaService: MatchMediaService,
    private readonly translateService: TranslateService,
    private readonly activityService: ActivityService,
    private readonly redirectionService: RedirectionService
  ) {}

  /**
   * Modules must be loaded here because they do not work in SSR
   */
  public ngAfterViewInit(): void {
    import('@fullcalendar/core').then((coreModule) => {
      Promise.all([
        import('@fullcalendar/core/locales-all').then(
          (module) => module.default
        ),
        import('@fullcalendar/daygrid').then((module) => module.default),
        import('@fullcalendar/list').then((module) => module.default),
        import('@fullcalendar/timegrid').then((module) => module.default),
      ])
        .then(([allLocalesModule, ...pluginsModules]) => {
          this.calendarOptions.locales = allLocalesModule;
          this.calendarOptions.plugins = pluginsModules;
          if (this.clickableTimeslots) {
            this.calendarOptions.eventClick = (event) =>
              this.redirectionService.redirect(
                `/organizer.timeslot.manage/${event.event.id}`
              );
          }

          this.calendarOptions.initialView =
            OrganizerSharedCalendarComponent.viewEnumToInitialView(this.view);

          this.calendar = new coreModule.Calendar(
            this.calendarComponent.nativeElement,
            this.calendarOptions
          );
        })
        .then(() => {
          // Calendar settings
          this.calendarApi.setOption(
            'locale',
            this.translateService.currentLang
          );

          this.calendarApi.setOption(
            'headerToolbar',
            this.calendarOptions.headerToolbar
          );
        })
        .then(() => {
          // Add calendar events
          this.buildEvents();

          if (this.isCreationMode) {
            const firstDate = this.creationTimeslots
              .map((creationTimeslot) => creationTimeslot.startDate)
              .reduce(
                (prevDate, nextDate) =>
                  prevDate.valueOf() < nextDate.valueOf() ? prevDate : nextDate,
                new Date('9999-12-31')
              );

            // Focus on the first next date
            this.calendarApi.gotoDate(firstDate);
          }
        })
        .then(() => {
          // Display calendar
          this.calendar.render();
        });
    });
  }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (!this.calendarApi) {
      return;
    }

    if (simpleChanges?.view?.firstChange === false) {
      const newViewEnum = simpleChanges.view.currentValue;
      this.updateView(newViewEnum);
    }

    if (
      simpleChanges?.timeslots?.firstChange === false &&
      simpleChanges?.timeslots?.currentValue?.length !==
        simpleChanges?.timeslots?.previousValue?.length
    ) {
      this.timeslots = [];
      this.getEventsFromTimeslots(simpleChanges.timeslots.currentValue);
    }
    if (simpleChanges?.creationTimeslots?.firstChange === false) {
      this.getEventsFromCreationTimeslots(
        simpleChanges.creationTimeslots.currentValue
      );
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.calendarOptions.headerToolbar = isMobileDevice
          ? CALENDAR_CONFIG_MOBILE
          : CALENDAR_CONFIG_DESKTOP;

        this.calendarApi?.setOption(
          'headerToolbar',
          this.calendarOptions.headerToolbar
        );

        this.view = isMobileDevice
          ? OrganizerSharedCalendarViewEnum.List
          : OrganizerSharedCalendarViewEnum.Months;

        this.updateView(this.view);
      })
    );

    this.buildViewsConfig();
    this.updateDateRange.emit({ ...this.currentCalendarInterval });
  }

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  private get calendarApi(): any {
    return this.calendar?.view?.calendar;
  }

  private get isCreationMode(): boolean {
    return this.creationTimeslots?.length > 0;
  }

  private static viewEnumToInitialView(
    viewEnum: OrganizerSharedCalendarViewEnum
  ): FullCalendarViewEnum {
    switch (viewEnum) {
      case OrganizerSharedCalendarViewEnum.Weeks:
        return FullCalendarViewEnum.TimeGridWeek;
      case OrganizerSharedCalendarViewEnum.Days:
        return FullCalendarViewEnum.TimeGridDay;
      case OrganizerSharedCalendarViewEnum.List:
        return FullCalendarViewEnum.ListWeek;
      case OrganizerSharedCalendarViewEnum.Months:
        return FullCalendarViewEnum.DayGridMonth;
      default:
        return FullCalendarViewEnum.DayGridMonth;
    }
  }

  private addCalendarEvents(events, toConcat = false): void {
    this.calendarOptions.eventDidMount = (i: EventMountArg) => {
      const privateIcon = this.createPrivateIcon();
      if (
        i.event.extendedProps.visibility !==
        ('PRIVATE' as ActivityVisibilityEnum)
      ) {
        return;
      }

      const titleStripped = new StripHtmlPipe().transform(i.event.title);
      const titleWithIcon = privateIcon?.outerHTML?.concat(titleStripped);

      if (['listWeek'].includes(i.view.type)) {
        i.el.querySelector('a[tabindex="0"]').innerHTML = titleWithIcon;
      }

      if (
        ['dayGridMonth', 'timeGridWeek', 'timeGridDay'].includes(i.view.type)
      ) {
        i.el.getElementsByClassName('fc-event-title')[0].innerHTML =
          titleWithIcon;
      }
    };

    this.calendarOptions.events = toConcat
      ? [...(this.calendarOptions.events as unknown[]), ...events]
      : events;

    this.calendarApi.setOption('events', this.calendarOptions.events);
  }

  private buildEvents(): void {
    this.getEventsFromTimeslots(this.timeslots);
    this.getEventsFromCreationTimeslots(this.creationTimeslots);
  }

  private buildViewsConfig(): void {
    this.tabButtonsGroupViewsConfig = this.viewsConfig.map((view) => ({
      value: view.value,
      text: this.translateService.instant(view.textKey),
    }));
  }

  private createPrivateIcon(): HTMLElement {
    const icon = document.createElement('i');
    icon.setAttribute(
      'aria-label',
      this.translateService.instant('a11y.activity.visibility.private')
    );
    icon.setAttribute('role', 'img');
    icon.className = 'vtmx-lock-line event-icon';

    return icon;
  }

  private getEventsFromCreationTimeslots(
    creationTimeslots: CalendarCreationTimeslotInterface[]
  ): void {
    const events = creationTimeslots.map((creationTimeslot) => {
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
      const event: any = {
        title: creationTimeslot.activity.name,
        color: '#3643ba',
      };

      if (creationTimeslot.isRecurrent) {
        event.daysOfWeek = creationTimeslot.daysOfWeek;
        event.startTime = creationTimeslot.startTime;
        event.endTime = creationTimeslot.endTime;
        event.startRecur = creationTimeslot.startDate;
        event.endRecur = creationTimeslot.endDate;
      } else {
        event.start = setDateTime(
          creationTimeslot.startDate,
          creationTimeslot.startTime
        );
        event.end = setDateTime(
          add(new Date(creationTimeslot.startDate), {
            days: creationTimeslot.activity.isoDuration?.days,
          }),
          creationTimeslot.endTime
        );
      }

      return event;
    });

    this.addCalendarEvents(events, true);
  }

  private getEventsFromTimeslots(timeslots: TimeslotInterface[]): void {
    const uniqueActivitiesIdentifiers = dedupe(
      timeslots.map((timeslot) => timeslot.activityIdentifier)
    );

    const activities$ = uniqueActivitiesIdentifiers.map((activityId) =>
      this.activityService.getActivity(activityId)
    );

    this.subscriptions.add(
      forkJoin(activities$)
        .pipe(
          map((activities) => {
            return timeslots.map((timeslot) => {
              const timeslotActivity = activities.find(
                (activity) =>
                  activity.sku.toString() === timeslot.activityIdentifier
              );
              const color =
                timeslot.remainingAttendeeCapacity === 0
                  ? '#ffcd4e'
                  : '#3643ba';

              return FullCalendarEventAdapter.adapt(
                timeslotActivity,
                timeslot,
                color
              );
            });
          })
        )
        .subscribe((events) =>
          this.addCalendarEvents(events, this.isCreationMode)
        )
    );
  }

  public updateView(view: OrganizerSharedCalendarViewEnum): void {
    const newView =
      OrganizerSharedCalendarComponent.viewEnumToInitialView(view);
    this.calendarApi?.changeView(newView);
  }
}
